import React from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link, Navigate } from "react-router-dom";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { getConditionBySlug } from "@/data/conditions";
import { useLocalize } from "@/data/localize";
import { SEO } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import * as Icons from "lucide-react";
import { AlertTriangle, Activity, Stethoscope, Pill, Salad } from "lucide-react";

export default function ConditionDetailPage() {
    const { t } = useTranslation();
    const { localize } = useLocalize();
    const { slug } = useParams();
    const raw = getConditionBySlug(slug);
    if (!raw) return <Navigate to="/conditions" replace />;
    const cond = localize(raw);

    const Icon = Icons[cond.icon] || Icons.Activity;

    return (
        <>
            <SEO title={cond.name} description={cond.summary} />
            <div className="container-page pt-8">
                <Breadcrumbs items={[{ to: "/conditions", label: t("nav.conditions") }, { label: cond.name }]} />
            </div>

            <section data-testid="condition-detail" className="container-page pt-6 pb-8">
                <div className="grid gap-8 lg:grid-cols-12 items-start">
                    <div className="lg:col-span-8">
                        <div className="flex items-center gap-3">
                            <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[color:var(--brand-teal-soft)] text-[color:var(--brand-teal-2)]">
                                <Icon className="h-5.5 w-5.5" />
                            </div>
                            <span className="chip-navy">{cond.category}</span>
                        </div>
                        <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold text-[color:var(--brand-navy)] leading-tight">
                            {cond.name}
                        </h1>
                        <p className="mt-3 text-lg text-[color:var(--text-muted)] leading-relaxed max-w-2xl">
                            {cond.summary}
                        </p>
                        <div className="mt-6 flex gap-3 flex-wrap">
                            <Button asChild size="lg" data-testid="condition-detail-cta">
                                <Link to="/referrals">{t("cta.askYourFamilyDoctor")}</Link>
                            </Button>
                            <Button asChild variant="outline" size="lg">
                                <Link to="/services">{t("cta.seeRelatedServices")}</Link>
                            </Button>
                        </div>
                    </div>
                    <aside className="lg:col-span-4 rounded-2xl border border-[color:var(--warning)]/25 bg-[color:var(--warning)]/8 p-6" style={{ background: "rgba(180,83,9,0.06)" }}>
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5" style={{ color: "var(--warning)" }} />
                            <p className="font-display text-base font-semibold text-[color:var(--brand-navy)]">{t("conditionDetail.whenToSeekCare")}</p>
                        </div>
                        <ul className="mt-3 space-y-2 text-sm text-[color:var(--text-muted)] pl-5 list-disc marker:text-[color:var(--warning)]">
                            {(cond.when_to_seek_care || []).map((w) => <li key={w}>{w}</li>)}
                        </ul>
                    </aside>
                </div>
            </section>

            <section className="container-page pb-8">
                <div className="grid gap-8 lg:grid-cols-2">
                    <article className="prose-clinical card-soft p-6">
                        <h2 className="flex items-center gap-2"><Activity className="h-5 w-5 text-[color:var(--brand-teal-2)]" /> {t("conditionDetail.symptoms")}</h2>
                        <ul>{(cond.symptoms || []).map((s) => <li key={s}>{s}</li>)}</ul>
                        <h2>{t("conditionDetail.possibleCauses")}</h2>
                        <ul>{(cond.causes || []).map((s) => <li key={s}>{s}</li>)}</ul>
                    </article>
                    <article className="prose-clinical card-soft p-6">
                        <h2 className="flex items-center gap-2"><Stethoscope className="h-5 w-5 text-[color:var(--brand-teal-2)]" /> {t("conditionDetail.howWeDiagnose")}</h2>
                        <ul>{(cond.diagnosis || []).map((s) => <li key={s}>{s}</li>)}</ul>
                        <h2 className="flex items-center gap-2"><Pill className="h-5 w-5 text-[color:var(--brand-teal-2)]" /> {t("conditionDetail.treatmentOptions")}</h2>
                        <ul>{(cond.treatments || []).map((s) => <li key={s}>{s}</li>)}</ul>
                    </article>
                </div>
            </section>

            <section className="container-page pb-8">
                <div className="card-soft p-6">
                    <h2 className="font-display text-xl font-semibold text-[color:var(--brand-navy)] flex items-center gap-2">
                        <Salad className="h-5 w-5 text-[color:var(--brand-teal-2)]" /> {t("conditionDetail.lifestylePrevention")}
                    </h2>
                    <ul className="mt-3 grid gap-2 sm:grid-cols-2 pl-5 list-disc marker:text-[color:var(--brand-teal)] text-sm text-[color:var(--text-muted)] leading-relaxed">
                        {(cond.lifestyle || []).map((l) => <li key={l}>{l}</li>)}
                    </ul>
                </div>
            </section>
        </>
    );
}
